import { DeleteOutline as DeleteOutlineIcon } from "@mui/icons-material";
import {
  Checkbox,
  Dialog,
  Divider,
  Grid,
  IconButton,
  ListItemText,
  MenuItem,
  TextField,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import Avatar from "../../../../assets/images/avatar-df.png";
import useNoti from "../../../../hooks/useNoti";
import { useTranslation } from "../../../../i18n/useTranslation";
import { AppNotifications } from "../../../../models";
import { EmployeeService } from "../../../../services/employee.service";
import {
  checkLinkImage,
  formatMDY,
  minutes_to_hhmm,
  renderErrorImage,
} from "../../../../utils";
import Loading from "../../../components/Loading";
import SimpleModalHeader from "../../../components/SimpleModalHeader";
import DefaultSwitch from "../../../components/Switch";
import { DEFAULT_IMG_ALT, TEXT_HOLDER } from "../../../constants";
import { MESSAGES } from "../../../constants/mock";
import { APP_ROUTES } from "../../../constants/module";
import "./index.scss";

const OTHER_TYPE_VALUE = "__other__";

const todayYmd = () => new Date().toISOString().slice(0, 10);

const Staff: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [pushNoti] = useNoti();
  const [overtime, setOvertime] = useState<boolean>(true);
  const { staffId }: { staffId: string } = useParams();
  const [employeeDetail, setEmployeeDetail] = useState<any>(null);
  const [violationTypes, setViolationTypes] = useState<string[]>([]);
  const [customType, setCustomType] = useState("");
  const [violationDate, setViolationDate] = useState(todayYmd());
  const [violationNote, setViolationNote] = useState("");
  const [savingViolation, setSavingViolation] = useState(false);
  const [deletingViolationId, setDeletingViolationId] = useState<string | null>(
    null
  );

  const suggestedTypes = useMemo(
    () => [
      t("staff.ruleViolationTypeAc"),
      t("staff.ruleViolationTypeLight"),
      t("staff.ruleViolationTypeDoor"),
    ],
    [t]
  );

  const typeOptions = useMemo(
    () => [
      ...suggestedTypes,
      OTHER_TYPE_VALUE,
    ],
    [suggestedTypes]
  );

  const otherLabel = t("staff.ruleViolationTypeOther");
  const hasOtherSelected = violationTypes.includes(OTHER_TYPE_VALUE);

  const fetchData: () => Promise<void> = async () => {
    try {
      const result = await EmployeeService.getEmployeeDetailById(staffId);
      if (result.data.data) {
        setEmployeeDetail(result.data.data);
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffId]);

  const ruleViolations = useMemo(() => {
    const list = Array.isArray(employeeDetail?.ruleViolations)
      ? [...employeeDetail.ruleViolations]
      : [];
    return list.sort((a: any, b: any) => {
      const dateCmp = String(b?.date || "").localeCompare(String(a?.date || ""));
      if (dateCmp !== 0) return dateCmp;
      return (
        new Date(b?.recordedAt || 0).getTime() -
        new Date(a?.recordedAt || 0).getTime()
      );
    });
  }, [employeeDetail]);

  const violationCounts = useMemo(() => {
    const map = new Map<string, number>();
    ruleViolations.forEach((item: any) => {
      const key = String(item?.type || "").trim();
      if (!key) return;
      map.set(key, (map.get(key) || 0) + 1);
    });
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [ruleViolations]);

  const resolvedTypes = useMemo(() => {
    const types: string[] = [];
    violationTypes.forEach((value) => {
      if (value === OTHER_TYPE_VALUE) {
        const custom = customType.trim();
        if (custom) types.push(custom);
        return;
      }
      const trimmed = String(value || "").trim();
      if (trimmed) types.push(trimmed);
    });
    return Array.from(new Set(types));
  }, [violationTypes, customType]);

  const renderSelectedTypes = (selected: unknown) => {
    const values = Array.isArray(selected) ? selected : [];
    if (!values.length) {
      return (
        <span className='staff-detail__violations__placeholder'>
          {t("staff.ruleViolationTypePlaceholder")}
        </span>
      );
    }
    return values
      .map((value) =>
        value === OTHER_TYPE_VALUE ? otherLabel : String(value)
      )
      .join(", ");
  };

  const handleAddViolation = async () => {
    if (!resolvedTypes.length || !violationDate) {
      pushNoti(AppNotifications.ERROR, t("staff.ruleViolationRequired"));
      return;
    }
    if (hasOtherSelected && !customType.trim()) {
      pushNoti(AppNotifications.ERROR, t("staff.ruleViolationCustomRequired"));
      return;
    }
    setSavingViolation(true);
    try {
      let latestEmployee = employeeDetail;
      for (const type of resolvedTypes) {
        const res = await EmployeeService.addRuleViolation(staffId, {
          type,
          date: violationDate,
          note: violationNote.trim(),
        });
        if (!res?.data?.data) {
          pushNoti(
            AppNotifications.ERROR,
            res?.data?.message || MESSAGES.ERROR
          );
          return;
        }
        latestEmployee = res.data.data;
      }
      setEmployeeDetail(latestEmployee);
      setViolationTypes([]);
      setCustomType("");
      setViolationDate(todayYmd());
      setViolationNote("");
      pushNoti(AppNotifications.SUCCESS, MESSAGES.SUCCESS);
    } catch (err: any) {
      pushNoti(
        AppNotifications.ERROR,
        err?.response?.data?.message || MESSAGES.ERROR
      );
    } finally {
      setSavingViolation(false);
    }
  };

  const handleDeleteViolation = async (violationId: string) => {
    if (!violationId) return;
    setDeletingViolationId(violationId);
    try {
      const res = await EmployeeService.deleteRuleViolation(
        staffId,
        violationId
      );
      if (res?.data?.data) {
        setEmployeeDetail(res.data.data);
        pushNoti(AppNotifications.SUCCESS, MESSAGES.SUCCESS);
      } else {
        pushNoti(
          AppNotifications.ERROR,
          res?.data?.message || MESSAGES.ERROR
        );
      }
    } catch (err: any) {
      pushNoti(
        AppNotifications.ERROR,
        err?.response?.data?.message || MESSAGES.ERROR
      );
    } finally {
      setDeletingViolationId(null);
    }
  };

  return (
    <Dialog
      open
      fullScreen
      className='staff-detail'
      PaperProps={{
        style: {
          backgroundColor: "#f0f3f8",
        },
      }}
    >
      <SimpleModalHeader
        title={t("staff.detailTitle")}
        onClose={() => {
          history.push(APP_ROUTES.STAFF);
        }}
      />
      {employeeDetail ? (
        <div className='staff-detail__content'>
          <div className='content-item staff-detail__content__left'>
            <div className='staff-detail__content__left__profile'>
              <img
                src={checkLinkImage(employeeDetail?.profilePhoto as string) || Avatar}
                alt={DEFAULT_IMG_ALT}
                onError={renderErrorImage()}
              />
              <div>
                <div className='staff-detail__content__left__profile__name'>
                  {employeeDetail?.fullName || TEXT_HOLDER}
                </div>
                <div className='staff-detail__content__left__profile__level'>
                  {employeeDetail?.level || TEXT_HOLDER}
                </div>
                <div className='staff-detail__content__left__profile__job'>
                  {employeeDetail?.position?.name || TEXT_HOLDER}
                </div>
              </div>
            </div>
            <div className='staff-detail__content__left__info'>
              <div className='content-item__title'>General Information</div>
              <Grid container rowSpacing={1}>
                <Grid item xs={4} className='content-item__label'>
                  Employee ID
                </Grid>
                <Grid item xs={8}>
                  {employeeDetail?.code || TEXT_HOLDER}
                </Grid>
                <Grid item xs={12} className='content-item__label'>
                  <Divider style={{ borderColor: "#F0F3F8" }} />
                </Grid>
                <Grid item xs={4} className='content-item__label'>
                  {t("staff.fullName")}
                </Grid>
                <Grid item xs={8}>
                  {employeeDetail?.fullName || TEXT_HOLDER}
                </Grid>
                <Grid item xs={12} className='content-item__label'>
                  <Divider style={{ borderColor: "#F0F3F8" }} />
                </Grid>
                <Grid item xs={4} className='content-item__label'>
                  {t("staff.checkinName")}
                </Grid>
                <Grid item xs={8}>
                  {employeeDetail?.checkinName || employeeDetail?.fullName || TEXT_HOLDER}
                </Grid>
                <Grid item xs={12} className='content-item__label'>
                  <Divider style={{ borderColor: "#F0F3F8" }} />
                </Grid>
                <Grid item xs={4} className='content-item__label'>
                  Email
                </Grid>
                <Grid item xs={8} style={{ color: "#0E76D9" }}>
                  {employeeDetail?.email || TEXT_HOLDER}
                </Grid>
                <Grid item xs={12} className='content-item__label'>
                  <Divider style={{ borderColor: "#F0F3F8" }} />
                </Grid>
                <Grid item xs={4} className='content-item__label'>
                  Phone
                </Grid>
                <Grid item xs={8}>
                  {employeeDetail?.phone || TEXT_HOLDER}
                </Grid>
                <Grid item xs={12} className='content-item__label'>
                  <Divider style={{ borderColor: "#F0F3F8" }} />
                </Grid>
                <Grid item xs={4} className='content-item__label'>
                  Gender
                </Grid>
                <Grid item xs={8}>
                  {employeeDetail?.gender || TEXT_HOLDER}
                </Grid>
                <Grid item xs={12} className='content-item__label'>
                  <Divider style={{ borderColor: "#F0F3F8" }} />
                </Grid>
                <Grid item xs={4} className='content-item__label'>
                  Address
                </Grid>
                <Grid item xs={8}>
                  {employeeDetail?.address || TEXT_HOLDER}
                </Grid>
                <Grid item xs={12} className='content-item__label'>
                  <Divider style={{ borderColor: "#F0F3F8" }} />
                </Grid>
                <Grid item xs={4} className='content-item__label'>
                  Date of birth
                </Grid>
                <Grid item xs={8}>
                  {formatMDY(employeeDetail?.dateOfBirth) || TEXT_HOLDER}
                </Grid>
                <Grid item xs={12} className='content-item__label'>
                  <Divider style={{ borderColor: "#F0F3F8" }} />
                </Grid>
                <Grid item xs={4} className='content-item__label'>
                  {t("staff.team")}
                </Grid>
                <Grid item xs={8}>
                  {employeeDetail.team?.name || TEXT_HOLDER}
                </Grid>
                <Grid item xs={12} className='content-item__label'>
                  <Divider style={{ borderColor: "#F0F3F8" }} />
                </Grid>
                <Grid item xs={4} className='content-item__label'>
                  {t("staff.position")}
                </Grid>
                <Grid item xs={8}>
                  {employeeDetail?.position?.name || TEXT_HOLDER}
                </Grid>
                <Grid item xs={12} className='content-item__label'>
                  <Divider style={{ borderColor: "#F0F3F8" }} />
                </Grid>
                <Grid item xs={4} className='content-item__label'>
                  {t("staff.level")}
                </Grid>
                <Grid item xs={8}>
                  {employeeDetail?.level || TEXT_HOLDER}
                </Grid>
                <Grid item xs={12} className='content-item__label'>
                  <Divider style={{ borderColor: "#F0F3F8" }} />
                </Grid>
                <Grid item xs={4} className='content-item__label'>
                  First day of work
                </Grid>
                <Grid item xs={8}>
                  {formatMDY(employeeDetail?.firstDayOfWork) || TEXT_HOLDER}
                </Grid>
                <Grid item xs={12} className='content-item__label'>
                  <Divider style={{ borderColor: "#F0F3F8" }} />
                </Grid>
                <Grid item xs={4} className='content-item__label'>
                  Last working day
                </Grid>
                <Grid item xs={8}>
                  {formatMDY(employeeDetail?.lastWorkingDay) || TEXT_HOLDER}
                </Grid>
              </Grid>
              <div className='content-item__title'>Insurance Information</div>
              <Grid container rowSpacing={1}>
                <Grid item xs={4} className='content-item__label'>
                  Social insurance
                </Grid>
                <Grid item xs={8}>
                  {employeeDetail?.insurance.social || TEXT_HOLDER}
                </Grid>
                <Grid item xs={12} className='content-item__label'>
                  <Divider style={{ borderColor: "#F0F3F8" }} />
                </Grid>
                <Grid item xs={4} className='content-item__label'>
                  Health insurance
                </Grid>
                <Grid item xs={8}>
                  {employeeDetail?.insurance.health || TEXT_HOLDER}
                </Grid>
                <Grid item xs={12} className='content-item__label'>
                  <Divider style={{ borderColor: "#F0F3F8" }} />
                </Grid>
                <Grid item xs={4} className='content-item__label'>
                  Other insurance policies
                </Grid>
                <Grid item xs={8}>
                  {employeeDetail?.insurance.others?.length
                    ? employeeDetail?.insurance.others.map((oth: string) => {
                        return oth;
                      })
                    : TEXT_HOLDER}
                </Grid>
              </Grid>

              <div className='content-item__title'>
                {t("staff.ruleViolationsTitle")}
              </div>
              <div className='staff-detail__violations'>
                <div className='staff-detail__violations__summary'>
                  {violationCounts.length === 0 ? (
                    <div className='staff-detail__violations__empty'>
                      {t("staff.ruleViolationsEmpty")}
                    </div>
                  ) : (
                    <div className='staff-detail__violations__chips'>
                      {violationCounts.map(([type, count]) => (
                        <span
                          key={type}
                          className='staff-detail__violations__chip'
                        >
                          {type}
                          <strong>{count}</strong>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className='staff-detail__violations__form'>
                  <div className='staff-detail__violations__form-title'>
                    {t("staff.ruleViolationAdd")}
                  </div>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={hasOtherSelected ? 6 : 5}>
                      <TextField
                        select
                        fullWidth
                        size='small'
                        label={t("staff.ruleViolationType")}
                        value={violationTypes}
                        onChange={(e) => {
                          const next = e.target.value;
                          setViolationTypes(
                            typeof next === "string" ? next.split(",") : next
                          );
                        }}
                        SelectProps={{
                          multiple: true,
                          displayEmpty: true,
                          renderValue: renderSelectedTypes,
                          MenuProps: {
                            className:
                              "select-menu-list staff-detail-violations-menu",
                            PaperProps: {
                              className: "staff-detail-violations-menu__paper",
                            },
                          },
                        }}
                      >
                        {typeOptions.map((value) => {
                          const label =
                            value === OTHER_TYPE_VALUE ? otherLabel : value;
                          return (
                            <MenuItem key={value} value={value}>
                              <Checkbox
                                checked={violationTypes.includes(value)}
                                size='small'
                              />
                              <ListItemText primary={label} />
                            </MenuItem>
                          );
                        })}
                      </TextField>
                    </Grid>
                    {hasOtherSelected && (
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size='small'
                          label={t("staff.ruleViolationTypeCustom")}
                          value={customType}
                          onChange={(e) => setCustomType(e.target.value)}
                        />
                      </Grid>
                    )}
                    <Grid item xs={12} md={hasOtherSelected ? 6 : 3}>
                      <TextField
                        fullWidth
                        size='small'
                        type='date'
                        label={t("staff.ruleViolationDate")}
                        InputLabelProps={{ shrink: true }}
                        value={violationDate}
                        onChange={(e) => setViolationDate(e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={hasOtherSelected ? 6 : 4}>
                      <TextField
                        fullWidth
                        size='small'
                        label={t("staff.ruleViolationNote")}
                        value={violationNote}
                        onChange={(e) => setViolationNote(e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <button
                        type='button'
                        className='btn-confirm-style staff-detail__violations__add-btn'
                        disabled={
                          savingViolation ||
                          !resolvedTypes.length ||
                          !violationDate ||
                          (hasOtherSelected && !customType.trim())
                        }
                        onClick={handleAddViolation}
                      >
                        {savingViolation
                          ? t("staff.ruleViolationSaving")
                          : t("staff.ruleViolationAdd")}
                      </button>
                    </Grid>
                  </Grid>
                </div>

                <div className='staff-detail__violations__history'>
                  <div className='staff-detail__violations__form-title'>
                    {t("staff.ruleViolationsHistory")}
                  </div>
                  {ruleViolations.length === 0 ? (
                    <div className='staff-detail__violations__empty'>
                      {t("staff.ruleViolationsEmpty")}
                    </div>
                  ) : (
                    <ul className='staff-detail__violations__list'>
                      {ruleViolations.map((item: any) => (
                        <li key={item._id}>
                          <div className='staff-detail__violations__row'>
                            <div>
                              <div className='staff-detail__violations__row-main'>
                                <span className='staff-detail__violations__row-date'>
                                  {item.date || TEXT_HOLDER}
                                </span>
                                <span className='staff-detail__violations__row-type'>
                                  {item.type || TEXT_HOLDER}
                                </span>
                              </div>
                              {item.note ? (
                                <div className='staff-detail__violations__row-note'>
                                  {item.note}
                                </div>
                              ) : null}
                              <div className='staff-detail__violations__row-meta'>
                                {t("staff.ruleViolationRecordedBy")}:{" "}
                                {item.recordedByName ||
                                  item.recordedByCode ||
                                  TEXT_HOLDER}
                              </div>
                            </div>
                            <IconButton
                              size='small'
                              aria-label={t("staff.ruleViolationDelete")}
                              disabled={deletingViolationId === item._id}
                              onClick={() => handleDeleteViolation(item._id)}
                            >
                              <DeleteOutlineIcon fontSize='small' />
                            </IconButton>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className='staff-detail__content__right'>
            <div className='content-item'>
              <div className='content-item__title'>Salary Information</div>
              <Grid container spacing={2}>
                <Grid item xs={6} className='content-item__label'>
                  Current salary
                </Grid>
                <Grid item xs={6} className='content-item__money'>
                  ${employeeDetail?.salary.current || TEXT_HOLDER}/
                  {employeeDetail?.salary.timeUnit}
                </Grid>
                <Grid item xs={12} className='content-item__label'>
                  <Divider style={{ borderColor: "#F0F3F8" }} />
                </Grid>
                <Grid item xs={6} className='content-item__label'>
                  Paid Leave Hours
                </Grid>
                <Grid item xs={6} className='content-item__money'>
                  {employeeDetail?.salary.paidLeaveHours
                    ? minutes_to_hhmm(employeeDetail?.salary.paidLeaveHours)
                    : TEXT_HOLDER}
                </Grid>
                <Grid item xs={12} className='content-item__label'>
                  <Divider style={{ borderColor: "#F0F3F8" }} />
                </Grid>
                <Grid item xs={6} className='content-item__label'>
                  Weekly work hours
                </Grid>
                <Grid item xs={6} className='content-item__money'>
                  {employeeDetail?.salary.weeklyWorkHours
                    ? minutes_to_hhmm(employeeDetail?.salary.weeklyWorkHours)
                    : TEXT_HOLDER}
                </Grid>
                <Grid item xs={12}>
                  <Grid container className='overtime-salary'>
                    <Grid
                      item
                      xs={6}
                      className='content-item__label'
                      style={{ paddingBottom: 8 }}
                    >
                      Overtime salary
                    </Grid>
                    <Grid
                      item
                      xs={6}
                      style={{ paddingBottom: 8, textAlign: "end" }}
                    >
                      <DefaultSwitch
                        checked={employeeDetail?.salary.overtimes?.length > 0}
                        onChange={() => {
                          setOvertime(!overtime);
                        }}
                      />
                    </Grid>
                    <div className='overtime-salary__list'>
                      {employeeDetail?.salary.overtimes?.map(
                        (over: any, idx: number) => {
                          return (
                            <Grid container key={idx}>
                              <Grid
                                item
                                xs={4}
                                className='overtime-salary__label'
                              >
                                On days
                              </Grid>
                              <Grid item xs={8}>
                                {over.onDays.map((day: string, idx: number) => {
                                  return (
                                    <span key={idx}>
                                      {day.charAt(0).toUpperCase() +
                                        day.slice(1)}
                                      {over.onDays?.length !== idx + 1 && (
                                        <span>,&nbsp;</span>
                                      )}
                                    </span>
                                  );
                                })}
                              </Grid>
                              <Grid
                                item
                                xs={4}
                                className='overtime-salary__label'
                              >
                                Start after
                              </Grid>
                              <Grid item xs={8}>
                                {over.startAfter}&nbsp;hours/day
                              </Grid>
                              <Grid
                                item
                                xs={4}
                                className='overtime-salary__label'
                              >
                                Multiplier
                              </Grid>
                              <Grid item xs={8}>
                                {over.multiplier} x Current salary
                              </Grid>
                              <Grid item xs={12}>
                                <Divider
                                  style={{
                                    borderColor: "#F0F3F8",
                                    margin: "2.2rem 0 2rem",
                                  }}
                                />
                              </Grid>
                            </Grid>
                          );
                        }
                      )}
                    </div>
                  </Grid>
                </Grid>
              </Grid>
            </div>
            <button
              className='btn-confirm-style'
              onClick={() =>
                history.push(
                  APP_ROUTES.STAFF_EDIT.replace(":staffId", employeeDetail._id)
                )
              }
            >
              Edit
            </button>
          </div>
        </div>
      ) : (
        <Loading />
      )}
    </Dialog>
  );
};

export default Staff;
